import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast, Toaster } from 'sonner';
import {
    ArrowLeft, Wallet, Loader2, TrendingDown, TrendingUp, Users, Truck, Shield, Settings2, Save,
} from 'lucide-react';
import { useStatsBlockGuard } from '../hooks/useStatsBlockGuard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (n) => {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return '-';
    return Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 }) + ' €';
};

function isoToday() {
    return new Date().toISOString().slice(0, 10);
}

function firstDayOfMonth(d = new Date()) {
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function firstDayOfYear(d = new Date()) {
    return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
}

function monthsAgo(n) {
    const d = new Date();
    d.setMonth(d.getMonth() - n);
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export default function StatistiquesBenefice() {
    useStatsBlockGuard('stats_benefice');
    const navigate = useNavigate();

    const [preset, setPreset] = useState('this_month');
    const [start, setStart] = useState(firstDayOfMonth());
    const [end, setEnd] = useState(isoToday());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [monthEdits, setMonthEdits] = useState({}); // { "YYYY-MM": {fa, fv} }
    const [savingMonth, setSavingMonth] = useState('');

    const applyPreset = useCallback((p) => {
        setPreset(p);
        if (p === 'this_month') { setStart(firstDayOfMonth()); setEnd(isoToday()); }
        else if (p === 'last_3') { setStart(monthsAgo(2)); setEnd(isoToday()); }
        else if (p === 'last_6') { setStart(monthsAgo(5)); setEnd(isoToday()); }
        else if (p === 'last_12') { setStart(monthsAgo(11)); setEnd(isoToday()); }
        else if (p === 'ytd') { setStart(firstDayOfYear()); setEnd(isoToday()); }
    }, []);

    const load = useCallback(async () => {
        if (!start || !end) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/statistiques/benefice`, {
                params: { start, end },
                withCredentials: true,
            });
            setData(res.data);
            // Seed edits with server values so the user can tweak
            const seed = {};
            (res.data?.months_details || []).forEach((m) => {
                seed[m.mois] = {
                    frais_assurance: m.frais_assurance || 0,
                    frais_variable: m.frais_variable || 0,
                };
            });
            setMonthEdits(seed);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur de chargement');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [start, end]);

    useEffect(() => { load(); }, [load]);

    const totals = data?.totals || {};
    const months = data?.months_details || [];

    // Live preview based on current edits (no need to save to see the bénéfice change)
    const preview = useMemo(() => {
        if (!data) return null;
        let fa = 0, fv = 0;
        months.forEach((m) => {
            const e = monthEdits[m.mois] || m;
            fa += Number(e.frais_assurance || 0);
            fv += Number(e.frais_variable || 0);
        });
        const ben = (totals.ca_ht || 0) - (totals.frais_dem || 0) - (totals.frais_monte_meuble || 0) - fa - fv;
        return {
            frais_assurance: fa,
            frais_variable: fv,
            benefice: Math.round(ben * 100) / 100,
            marge_pct: totals.ca_ht ? Math.round(((ben / totals.ca_ht) * 100) * 10) / 10 : null,
        };
    }, [data, months, monthEdits, totals]);

    const saveMonth = async (mois) => {
        const e = monthEdits[mois] || {};
        setSavingMonth(mois);
        try {
            await axios.post(`${API_URL}/api/statistiques/benefice/frais-mois`,
                { mois, frais_assurance: Number(e.frais_assurance || 0), frais_variable: Number(e.frais_variable || 0) },
                { withCredentials: true });
            toast.success(`Frais enregistrés pour ${mois}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
        } finally {
            setSavingMonth('');
        }
    };

    const KPI = ({ label, value, sub, icon: Icon, color, trend }) => (
        <Card className="shadow-sm">
            <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-xs uppercase text-gray-500 tracking-wide">{label}</p>
                        <p className="text-2xl font-bold text-[#1B365D] mt-1 truncate">{value}</p>
                        {sub ? <p className="text-xs text-gray-400 mt-0.5">{sub}</p> : null}
                    </div>
                    <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1A` }}>
                        {trend === 'up' ? <TrendingUp className="h-5 w-5" style={{ color }} /> :
                         trend === 'down' ? <TrendingDown className="h-5 w-5" style={{ color }} /> :
                         <Icon className="h-5 w-5" style={{ color }} />}
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <Layout>
            <Toaster position="top-right" />
            <div className="space-y-6 animate-fadeIn" data-testid="benefice-page">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/statistiques')} className="h-8 w-8 p-0" data-testid="benefice-back">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="h-10 w-10 rounded-full bg-[#05966914] flex items-center justify-center">
                        <Wallet className="h-5 w-5 text-[#059669]" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Bénéfice
                        </h1>
                        <p className="text-gray-500 mt-0.5 text-sm">CA HT − frais déménageurs − monte-meuble − assurance − variables</p>
                    </div>
                </div>

                {/* Period picker */}
                <Card className="shadow-sm">
                    <CardContent className="p-4 flex flex-col md:flex-row md:items-end gap-3">
                        <div className="flex-1 min-w-[180px]">
                            <label className="text-xs uppercase text-gray-500 tracking-wide">Période rapide</label>
                            <Select value={preset} onValueChange={applyPreset}>
                                <SelectTrigger className="mt-1" data-testid="benefice-preset">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="this_month">Ce mois</SelectItem>
                                    <SelectItem value="last_3">3 derniers mois</SelectItem>
                                    <SelectItem value="last_6">6 derniers mois</SelectItem>
                                    <SelectItem value="last_12">12 derniers mois</SelectItem>
                                    <SelectItem value="ytd">Année en cours</SelectItem>
                                    <SelectItem value="custom">Personnalisé</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1 min-w-[160px]">
                            <label className="text-xs uppercase text-gray-500 tracking-wide">Début</label>
                            <Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPreset('custom'); }}
                                className="mt-1" data-testid="benefice-start" />
                        </div>
                        <div className="flex-1 min-w-[160px]">
                            <label className="text-xs uppercase text-gray-500 tracking-wide">Fin</label>
                            <Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPreset('custom'); }}
                                className="mt-1" data-testid="benefice-end" />
                        </div>
                        <Button onClick={load} disabled={loading} className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white md:self-end">
                            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                            Actualiser
                        </Button>
                    </CardContent>
                </Card>

                {loading ? (
                    <Card><CardContent className="p-10 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </CardContent></Card>
                ) : !data ? (
                    <Card><CardContent className="p-10 text-center text-gray-500">
                        Aucune donnée
                    </CardContent></Card>
                ) : (
                    <>
                        {/* KPI row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <KPI label="CA HT" value={fmtEur(totals.ca_ht)} sub={`${totals.ca_count || 0} prestations`} icon={Users} color="#1B365D" />
                            <KPI label="Frais déménageurs" value={fmtEur(totals.frais_dem)} sub="Affectations de la période" icon={Truck} color="#EF4444" trend="down" />
                            <KPI label="Frais monte-meuble" value={fmtEur(totals.frais_monte_meuble)} sub="Issu des devis" icon={Settings2} color="#F59E0B" trend="down" />
                            <KPI
                                label="Bénéfice"
                                value={fmtEur(preview?.benefice)}
                                sub={preview?.marge_pct != null ? `Marge ${preview.marge_pct} %` : '—'}
                                icon={Wallet}
                                color={preview && preview.benefice >= 0 ? '#059669' : '#EF4444'}
                                trend={preview && preview.benefice >= 0 ? 'up' : 'down'}
                            />
                        </div>

                        {/* Breakdown card */}
                        <Card className="shadow-sm">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base text-[#1B365D]">Détail du calcul</CardTitle>
                            </CardHeader>
                            <CardContent className="p-5 pt-2">
                                <div className="space-y-2 max-w-lg">
                                    <Row label="CA HT" value={fmtEur(totals.ca_ht)} positive />
                                    <Row label="− Frais déménageurs" value={fmtEur(totals.frais_dem)} />
                                    <Row label="− Frais monte-meuble" value={fmtEur(totals.frais_monte_meuble)} />
                                    <Row label="− Frais assurance" value={fmtEur(preview?.frais_assurance)} />
                                    <Row label="− Frais variables" value={fmtEur(preview?.frais_variable)} />
                                    <div className="border-t pt-2 mt-2 flex items-center justify-between">
                                        <span className="font-semibold text-[#1B365D]">= Bénéfice net</span>
                                        <span className={`font-bold text-lg ${(preview?.benefice ?? 0) >= 0 ? 'text-[#059669]' : 'text-red-600'}`}>
                                            {fmtEur(preview?.benefice)}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Monthly table: full detail per month */}
                        <Card className="shadow-sm">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base text-[#1B365D] flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-[#C9A962]" />
                                    Détail par mois
                                </CardTitle>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    Saisissez les frais assurance et variables pour chaque mois. Le bénéfice mensuel se met à jour automatiquement.
                                </p>
                            </CardHeader>
                            <CardContent className="p-0">
                                {months.length === 0 ? (
                                    <p className="text-sm text-gray-400 p-6">Aucun mois dans la période sélectionnée.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                                                <tr>
                                                    <th className="text-left py-3 px-4">Mois</th>
                                                    <th className="text-right py-3 px-3">CA HT</th>
                                                    <th className="text-right py-3 px-3">Presta.</th>
                                                    <th className="text-right py-3 px-3">Frais dem</th>
                                                    <th className="text-right py-3 px-3">Monte-meuble</th>
                                                    <th className="text-left py-3 px-3">Assurance (€)</th>
                                                    <th className="text-left py-3 px-3">Variables (€)</th>
                                                    <th className="text-right py-3 px-3">Bénéfice</th>
                                                    <th className="text-right py-3 px-4"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {months.map((m) => {
                                                    const e = monthEdits[m.mois] || { frais_assurance: 0, frais_variable: 0 };
                                                    const fa = Number(e.frais_assurance || 0);
                                                    const fv = Number(e.frais_variable || 0);
                                                    const ben = (m.ca_ht || 0) - (m.frais_dem || 0) - (m.frais_monte_meuble || 0) - fa - fv;
                                                    const marge = m.ca_ht > 0 ? Math.round((ben / m.ca_ht) * 1000) / 10 : null;
                                                    // Detect unsaved changes vs. server values
                                                    const dirty = Number(fa) !== Number(m.frais_assurance || 0)
                                                        || Number(fv) !== Number(m.frais_variable || 0);
                                                    return (
                                                        <tr key={m.mois} className="border-t hover:bg-gray-50/40">
                                                            <td className="py-2 px-4 font-semibold text-[#1B365D]">{m.mois}</td>
                                                            <td className="py-2 px-3 text-right text-gray-700">{fmtEur(m.ca_ht)}</td>
                                                            <td className="py-2 px-3 text-right text-gray-500">{m.ca_count || 0}</td>
                                                            <td className="py-2 px-3 text-right text-red-600">{fmtEur(m.frais_dem)}</td>
                                                            <td className="py-2 px-3 text-right text-amber-600">{fmtEur(m.frais_monte_meuble)}</td>
                                                            <td className="py-2 px-3">
                                                                <Input
                                                                    type="number" step="0.01" min="0"
                                                                    className="h-8 w-28"
                                                                    value={e.frais_assurance}
                                                                    onChange={(ev) => setMonthEdits({
                                                                        ...monthEdits,
                                                                        [m.mois]: { ...e, frais_assurance: ev.target.value },
                                                                    })}
                                                                    data-testid={`fa-${m.mois}`}
                                                                />
                                                            </td>
                                                            <td className="py-2 px-3">
                                                                <Input
                                                                    type="number" step="0.01" min="0"
                                                                    className="h-8 w-28"
                                                                    value={e.frais_variable}
                                                                    onChange={(ev) => setMonthEdits({
                                                                        ...monthEdits,
                                                                        [m.mois]: { ...e, frais_variable: ev.target.value },
                                                                    })}
                                                                    data-testid={`fv-${m.mois}`}
                                                                />
                                                            </td>
                                                            <td className={`py-2 px-3 text-right font-bold ${ben >= 0 ? 'text-[#059669]' : 'text-red-600'}`}>
                                                                {fmtEur(Math.round(ben * 100) / 100)}
                                                                {marge != null && (
                                                                    <div className="text-[10px] font-normal text-gray-400">
                                                                        {marge} %
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="py-2 px-4 text-right">
                                                                <Button
                                                                    size="sm"
                                                                    variant={dirty ? 'default' : 'outline'}
                                                                    className={dirty ? 'bg-[#1B365D] hover:bg-[#2A4A7A] text-white' : ''}
                                                                    disabled={savingMonth === m.mois || !dirty}
                                                                    onClick={() => saveMonth(m.mois)}
                                                                    data-testid={`save-${m.mois}`}
                                                                >
                                                                    {savingMonth === m.mois
                                                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                                                        : (<><Save className="w-3 h-3 mr-1" /> Enregistrer</>)}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot className="bg-gray-50/60 text-sm font-semibold text-[#1B365D]">
                                                <tr>
                                                    <td className="py-3 px-4">Total</td>
                                                    <td className="py-3 px-3 text-right">{fmtEur(totals.ca_ht)}</td>
                                                    <td className="py-3 px-3 text-right">{totals.ca_count || 0}</td>
                                                    <td className="py-3 px-3 text-right text-red-600">{fmtEur(totals.frais_dem)}</td>
                                                    <td className="py-3 px-3 text-right text-amber-600">{fmtEur(totals.frais_monte_meuble)}</td>
                                                    <td className="py-3 px-3 text-gray-700">{fmtEur(preview?.frais_assurance)}</td>
                                                    <td className="py-3 px-3 text-gray-700">{fmtEur(preview?.frais_variable)}</td>
                                                    <td className={`py-3 px-3 text-right ${preview && preview.benefice >= 0 ? 'text-[#059669]' : 'text-red-600'}`}>
                                                        {fmtEur(preview?.benefice)}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </Layout>
    );
}

function Row({ label, value, positive }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{label}</span>
            <span className={positive ? 'font-semibold text-[#1B365D]' : 'text-gray-700'}>{value}</span>
        </div>
    );
}
